import { FC, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { format } from 'date-fns';
import { es } from 'date-fns/locale/es';
import {
  CircleXIcon,
  HeartIcon,
  HeartOffIcon,
  TriangleAlertIcon,
  CheckCircle2Icon,
  EllipsisVerticalIcon,
  Loader2Icon,
  MapPinIcon,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL, ANIMAL_TYPES, ANIMAL_GENDERS } from '@/constants';
import { ReportPub } from '@/components/ReportPub/ReportPub';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';
import { Dialog } from '@/components/ui/dialog';
import { useAuthToken } from '@/hooks/useAuthToken';

interface PostParams {
  pet: Pet;
  refetch: () => void;
}

const STATE_STYLES = {
  lost: { fill: 'fill-lost', verb: 'SE BUSCA' },
  found: { fill: 'fill-found', verb: '¿LO CONOCÉS?' },
} as const;

export const Post: FC<PostParams> = ({ pet, refetch }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isTokenExpired, token, userIdFromToken } = useAuthToken();

  const date = useMemo(() => {
    if (pet.status === 'found' && pet.date_found) {
      return format(new Date(pet.date_found), 'dd/MM', { locale: es });
    }
    if (pet.status === 'lost' && pet.date_lost) {
      return format(new Date(pet.date_lost), 'dd/MM', { locale: es });
    }
    return format(new Date(), 'dd/MM', { locale: es });
  }, [pet.status, pet.date_found, pet.date_lost]);

  const daysAgo = useMemo(() => {
    const target = pet.status === 'lost' ? pet.date_lost : pet.date_found;
    if (!target) return null;
    const diff = (Date.now() - new Date(target).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.floor(diff));
  }, [pet.status, pet.date_lost, pet.date_found]);

  const zone = useMemo(() => {
    if (!pet.location?.address) return 'Zona no disponible';
    const { road, house_number, city } = pet.location.address;
    const label = `${road ?? ''} ${house_number ?? ''}${road && city ? ', ' : ''}${city ?? ''}`.trim();
    return label.length > 36 ? `${label.slice(0, 36)}…` : label || 'Zona no disponible';
  }, [pet.location]);

  const genderGlyph = pet.gender === 'm' ? '♂' : pet.gender === 'f' ? '♀' : '?';
  const kindLabel = ANIMAL_TYPES[pet.type]?.toUpperCase() ?? pet.type.toUpperCase();
  const genderLabel = ANIMAL_GENDERS[pet.gender] ?? pet.gender;

  const [isSolvingCase, setIsSolvingCase] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const thumbnail = pet.gallery?.[0];

  const handleAddFavorite = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (token && !isTokenExpired(token)) {
        try {
          const response = await axios.post(
            `${API_BASE_URL}/favorites/`,
            { pet: pet.id, user: userIdFromToken(token) },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (response.status === 200 || response.status === 201) {
            toast.success('Agregada a favoritos');
          }
        } catch {
          toast.info('Ya está en favoritos');
        }
        return;
      }
      toast.info('Inicia sesión para agregar a favoritos');
      navigate('/login');
    },
    [token, pet.id, navigate, isTokenExpired, userIdFromToken],
  );

  const handleRemoveFavorite = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (token && !isTokenExpired(token)) {
        await axios.delete(`${API_BASE_URL}/favorites/${pet.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Eliminado de favoritos');
        refetch();
        return;
      }
      toast.info('Inicia sesión para eliminar de favoritos');
      navigate('/login');
    },
    [token, pet.id, refetch, isTokenExpired, navigate],
  );

  const handleDeletePost = useCallback(async () => {
    if (token && !isTokenExpired(token)) {
      try {
        setIsDeletingPost(true);
        await axios.delete(`${API_BASE_URL}/pets/${pet.id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        window.location.reload();
      } catch (error) {
        setIsDeletingPost(false);
        console.error('Error al eliminar el post:', error);
      }
      setIsDeletingPost(false);
      return;
    }
    toast.info('Inicia sesión para eliminar el post');
    navigate('/login');
  }, [token, pet.id, isTokenExpired, navigate]);

  const handleSolvedCase = async () => {
    if (token && !isTokenExpired(token)) {
      try {
        setIsSolvingCase(true);
        const response = await axios.patch(
          `${API_BASE_URL}/pets/${pet.id}/`,
          { solved_case: true },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (response.status === 200 || response.status === 201) {
          refetch();
        }
      } catch (error) {
        setIsSolvingCase(false);
        console.error('Error al resolver el caso:', error);
      }
      setIsSolvingCase(false);
      return;
    }
    toast.info('Inicia sesión para resolver el caso');
    navigate('/login');
  };

  const goToDetail = () => navigate(`/pet/${pet.id}/`);

  const isOwnerOrAdminPath = pathname === '/my-publications';
  const isFavPath = pathname === '/my-favorites';
  const isSolvedPath = pathname === '/solved-cases';
  const isListingPath = pathname === '/found' || pathname === '/lost';

  const state = pet.status;
  const styling = STATE_STYLES[state];
  const urgent = state === 'lost' && daysAgo !== null && daysAgo <= 1;

  const petNameDisplay =
    state === 'lost' ? pet.name || 'Sin nombre' : pet.name?.trim() ? pet.name : 'Sin nombre';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter') goToDetail();
      }}
      className={`nb relative cursor-pointer overflow-hidden ${pet.solved_case ? 'opacity-70' : ''}`}
      style={{ background: 'var(--nb-paper)' }}
    >
      {pet.solved_case && <div className="nb-stamp-rotated">✓ RESUELTO</div>}

      {/* Verb / status bar */}
      <div
        className={`flex items-center justify-between px-2 py-1 ${styling.fill}`}
        style={{ borderBottom: '3px solid var(--nb-ink)' }}
      >
        <span
          className="text-[10px] font-black uppercase tracking-[0.06em]"
          style={{ fontFamily: '"Archivo Black", sans-serif' }}
        >
          {styling.verb}
        </span>
        {urgent && (
          <span
            className="nb-blink text-[10px] font-bold uppercase"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            ● vivo
          </span>
        )}
      </div>

      {/* Photo */}
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: '1 / 1', borderBottom: '3px solid var(--nb-ink)', background: '#e8e3d5' }}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={petNameDisplay}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
            className={`h-full w-full object-cover transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        ) : (
          <div className="nb-hatch absolute inset-0" />
        )}

        {/* Action overlay */}
        <div className="absolute right-2 top-2 flex gap-1.5">
          {(isListingPath || isFavPath) && !isFavPath && (
            <button
              aria-label="Agregar a favoritos"
              onClick={handleAddFavorite}
              className="nb-btn nb-btn-sm bg-paper"
              style={{ padding: '4px 6px' }}
            >
              <HeartIcon size={14} />
            </button>
          )}
          {isFavPath && (
            <button
              aria-label="Quitar de favoritos"
              onClick={handleRemoveFavorite}
              className="nb-btn nb-btn-sm"
              style={{ padding: '4px 6px', background: 'var(--nb-urgent)' }}
            >
              <HeartOffIcon size={14} />
            </button>
          )}
          {isListingPath && (
            <button
              aria-label="Reportar publicación"
              onClick={(e) => {
                e.stopPropagation();
                setIsReportDialogOpen((v) => !v);
              }}
              className="nb-btn nb-btn-sm bg-paper"
              style={{ padding: '4px 6px' }}
            >
              <TriangleAlertIcon size={14} />
            </button>
          )}
          {isOwnerOrAdminPath && (
            <Dialog>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Acciones"
                    onClick={(e) => e.stopPropagation()}
                    className="nb-btn nb-btn-sm bg-paper"
                    style={{ padding: '4px 6px' }}
                  >
                    <EllipsisVerticalIcon size={14} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuLabel onClick={handleSolvedCase}>
                    <div className="flex cursor-pointer items-center gap-1">
                      <div className="h-6 w-6">
                        {isSolvingCase ? (
                          <Loader2Icon className="animate-spin" />
                        ) : (
                          <CheckCircle2Icon fill={pet.solved_case ? 'green' : 'transparent'} />
                        )}
                      </div>
                      ¿Resuelto?
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel
                    className="rounded-lg bg-red-500"
                    onClick={() => setIsConfirmDialogOpen((v) => !v)}
                  >
                    <div className="flex cursor-pointer items-center gap-1">
                      <div className="h-6 w-6">
                        {isDeletingPost ? <Loader2Icon className="animate-spin" /> : <CircleXIcon />}
                      </div>
                      Eliminar
                    </div>
                  </DropdownMenuLabel>
                  <ConfirmationDialog
                    open={isConfirmDialogOpen}
                    onClose={() => setIsConfirmDialogOpen(false)}
                    onConfirm={handleDeletePost}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </Dialog>
          )}
        </div>

        <ReportPub
          open={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          post={pet}
        />

        {/* Filename tag — evokes the evidence-bag mono tag in the mock */}
        <div
          className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em]"
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            background: 'var(--nb-ink)',
            color: 'var(--nb-paper)',
          }}
        >
          #{String(pet.id).padStart(4, '0')}
        </div>
      </div>

      <div className="px-2.5 py-2.5">
        <div className="font-display text-[18px] leading-none">{petNameDisplay}</div>
        <div
          className="mt-1 flex justify-between text-[9px] font-bold uppercase"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          <span>
            {kindLabel} · {genderLabel} {genderGlyph}
          </span>
          <span className="opacity-60">{date}</span>
        </div>
        <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold">
          <MapPinIcon size={11} />
          <span className="truncate">{zone}</span>
        </div>

        {isSolvedPath && daysAgo !== null && (
          <div
            className="fill-solved mt-1.5 inline-block border-2 px-1.5 py-0.5 text-[10px]"
            style={{ borderColor: 'var(--nb-ink)', fontFamily: '"Archivo Black", sans-serif' }}
          >
            ✓ VOLVIÓ A CASA · {daysAgo}d
          </div>
        )}
      </div>
    </div>
  );
};
